package com.partejar.app.feature.equipe.data.remote

import com.partejar.app.core.network.ApiConfig
import com.partejar.app.core.network.commercialConstraintOrNull
import com.partejar.app.feature.entitlements.data.CommercialConstraintException
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.equipe.data.EquipeAccessException
import com.partejar.app.feature.equipe.data.EquipeDataException
import com.partejar.app.feature.equipe.data.EquipeDataSource
import com.partejar.app.feature.equipe.data.EquipeMember
import com.partejar.app.feature.equipe.data.EquipeMemberMissingException
import com.partejar.app.feature.equipe.data.EquipeOperationException
import com.partejar.app.feature.equipe.data.EquipeUnavailableException
import com.partejar.app.feature.equipe.data.TeamInviteInput
import com.partejar.app.feature.equipe.data.TeamInviteResult
import com.partejar.app.feature.equipe.data.TeamMemberUpdate
import com.partejar.app.feature.equipe.data.ProfessionalRegistration
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class EquipeRemoteDataSource(
    private val client: HttpClient,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository,
    private val mapper: EquipeRemoteMapper
) : EquipeDataSource {
    override suspend fun list(): List<EquipeMember> = request {
        val response = client.get(apiConfig.endpoint("/equipe")) { workspaceHeader() }
        when (response.status) {
            HttpStatusCode.OK -> response.body<List<EquipeMemberDto>>().map(mapper::map)
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw EquipeAccessException()
            else -> throw EquipeUnavailableException()
        }
    }

    override suspend fun invite(input: TeamInviteInput): TeamInviteResult = request {
        val response = client.post(apiConfig.endpoint("/equipe/convites")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(CriarConviteEquipeDto(input.name.trim(), input.email.trim(), input.profileCode, input.professionalCategory))
        }
        when (response.status) {
            HttpStatusCode.Created -> response.body<ConviteEquipeDto>().let { TeamInviteResult(it.link, it.expiraEm) }
            HttpStatusCode.Conflict -> throw response.commercialConstraintOrNull()
                ?: response.operationError("O convite não pôde ser criado.")
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw EquipeAccessException(
                response.errorDetails() ?: "Você não tem permissão para convidar pessoas."
            )
            HttpStatusCode.BadRequest -> throw response.operationError("Revise os dados do convite.")
            else -> throw EquipeUnavailableException()
        }
    }

    override suspend fun update(memberId: Long, update: TeamMemberUpdate): EquipeMember = request {
        val response = client.patch(apiConfig.endpoint("/equipe/$memberId")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(AtualizarMembroEquipeDto(update.profileCode, update.professionalCategory, update.professionalRegistrationId, update.active))
        }
        when (response.status) {
            HttpStatusCode.OK -> mapper.map(response.body())
            HttpStatusCode.Conflict -> throw response.commercialConstraintOrNull()
                ?: response.operationError("A alteração não pôde ser concluída.")
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw EquipeAccessException(
                response.errorDetails() ?: "Você não tem permissão para alterar esta pessoa."
            )
            HttpStatusCode.NotFound -> throw EquipeMemberMissingException()
            HttpStatusCode.BadRequest -> throw response.operationError("Revise as alterações desta pessoa.")
            else -> throw EquipeUnavailableException()
        }
    }

    private suspend fun HttpResponse.operationError(fallback: String) =
        EquipeOperationException(errorDetails() ?: fallback)

    private suspend fun HttpResponse.errorDetails(): String? = runCatching {
        Json { ignoreUnknownKeys = true }
            .decodeFromString<EquipeErrorDto>(bodyAsText())
            .details
            ?.takeIf(String::isNotBlank)
    }.getOrNull()

    override suspend fun registrations(memberId: Long): List<ProfessionalRegistration> = request {
        val response = client.get(apiConfig.endpoint("/equipe/$memberId/registros-profissionais")) { workspaceHeader() }
        when (response.status) {
            HttpStatusCode.OK -> response.body<List<RegistroProfissionalEquipeDto>>().map(mapper::registration)
            HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden -> throw EquipeAccessException()
            else -> throw EquipeUnavailableException()
        }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.workspaceHeader() {
        val workspaceId = currentWorkspaceRepository.currentWorkspaceId() ?: throw EquipeAccessException()
        header("X-Workspace-Id", workspaceId)
    }

    private suspend fun <T> request(block: suspend () -> T): T = try {
        block()
    } catch (error: CancellationException) {
        throw error
    } catch (error: CommercialConstraintException) {
        throw error
    } catch (error: EquipeDataException) {
        throw error
    } catch (error: Throwable) {
        throw EquipeUnavailableException(error)
    }

}

@Serializable
private data class EquipeErrorDto(val details: String? = null)
